/* eslint-disable @typescript-eslint/naming-convention */
import {
  styled,
  Button,
  FormControl,
  Box,
  CircularProgress,
  FormHelperText,
} from '@mui/material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  InputLabel,
  MenuItem,
  colors,
} from '@resiliatech/birdsong'
import { Limits } from 'TypeDefs/FeatureGatingRule/FeatureGatingRule'
import { funderUserRoles, userRoles } from 'Context/Constants/constants'
import { useAppState } from 'Context/AppContext/AppContext'
import CREATE_MANY_NONPROFIT_ORG_USERS from 'Apollo/Mutations/CreateManyNonprofitOrgUsers'
import { useLazyQuery, useMutation } from '@apollo/client'
import { FormEvent, useState } from 'react'
import GET_NONPROFIT_ORG_TEAM_QUERY from 'Apollo/Queries/GetNonprofitOrgTeam'
import GET_FUNDER_USERS from 'Apollo/Queries/GetFunderUsers'
import FIND_DUPLICATE_USERS from 'Apollo/Mutations/FindDuplicateUsers'
import { NonprofitOrgInviteType } from 'Generated/graphql-types'
import { team } from 'Modules/Analytics/trackActionInterface/Account/team'
import { analytics } from 'Modules/Analytics'
import { getOptionLabel } from 'Components/SongComponents/Util'
import CREATE_MANY_FUNDER_ORG_USERS from 'Apollo/Mutations/CreateManyFunderUsers'
import { AddMembersAlert } from './AddMembersAlert'
import AddImage from './addImage.png'
import { findInvalidEmails, parseEmails } from './utils'

type Props = {
  callback?: { function: (args?: unknown) => void; appendedMessage?: string }
  open?: boolean
  setOpen: (isOpen: boolean) => void
  seats?: Limits
  setShowBirdsongSnackbar: (value: React.SetStateAction<boolean>) => void
  setBirdsongSnackbarMessage: (value: React.SetStateAction<string>) => void
  withDialog?: boolean
  isFunder?: boolean
}

export const AddMembersDialog = ({
  callback,
  open,
  setOpen,
  seats,
  setShowBirdsongSnackbar,
  setBirdsongSnackbarMessage,
  withDialog = true,
  isFunder = false,
}: Props) => {
  const {
    nonprofitOrgConfig: {
      platformPlan,
      orgId,
      role: resiliaRole,
      nonprofitOrgRole,
    },
    userConfig: { email: currentUserEmail, id: inviterUserId },
    funderOrgConfig: {
      orgId: funderOrgId,
      user: { role: funderUserRole, funderOrgRole: funderUserOrgRole },
    },
  } = useAppState()
  const [emails, setEmails] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasValidationError, setHasValidationError] = useState(false)
  const [readyToSubmit, setReadyToSubmit] = useState(false)
  const [validationMessage, setValidationMessage] = useState('')
  const [role, setRole] = useState('')
  const [successCount, setSuccessCount] = useState<null | number>(null)
  const hasUnlimitedInvites = seats?.limit === null
  const roleList = isFunder ? funderUserRoles : userRoles

  //  ADDING NONPROFIT USERS
  // todo: rework getDuplicateUsers to be work for funders
  const [getDuplicateUsers] = useLazyQuery(FIND_DUPLICATE_USERS)
  const [createUsers] = useMutation(CREATE_MANY_NONPROFIT_ORG_USERS, {
    onCompleted: ({ createManyNonprofitOrgUsers }) => {
      onCreateUserCompleted(createManyNonprofitOrgUsers)
    },
    onError: () => {
      setIsLoading(false)
    },
    refetchQueries: [
      {
        query: GET_NONPROFIT_ORG_TEAM_QUERY,
        variables: {
          nonprofitOrgId: orgId,
          planName: platformPlan,
          featureName: 'Team',
        },
      },
    ],
    awaitRefetchQueries: true,
  })

  // ADDING FUNDER USERS
  const [createFunderUsers] = useMutation(CREATE_MANY_FUNDER_ORG_USERS, {
    onCompleted: ({ createManyFunderUsers }) => {
      onCreateUserCompleted(createManyFunderUsers)
    },
    onError: () => {
      setIsLoading(false)
    },
    refetchQueries: [
      {
        query: GET_FUNDER_USERS,
        variables: {
          funderOrgId,
        },
      },
    ],
    awaitRefetchQueries: true,
  })

  const onCreateUserCompleted = (usersCreated: any[]) => {
    setEmails('')
    setOpen(false)
    setIsLoading(false)

    const { length } = usersCreated

    if (length) {
      setBirdsongSnackbarMessage(
        `${length} member${length === 1 ? '' : 's'} invited via email${
          callback?.appendedMessage ?? ''
        }`
      )
      setShowBirdsongSnackbar(true)
    }
    if (!withDialog) {
      setSuccessCount(length)
    }
  }

  // Segment
  const dispatchTeamMemberInvited = analytics.trackActionCreator(
    team.Add_Member_Submitted
  )
  const dispatchSpecificTeamMemberAdded = analytics.trackActionCreator(
    team.Specific_Team_Member_Added
  )

  const getDupeUsers = async (uniqueEmails: string[]) => {
    if (isFunder) {
      return { emails: [] }
    }
    const {
      data: { findDuplicateUsers },
    } = await getDuplicateUsers({
      variables: {
        emails: uniqueEmails,
        nonprofitOrgId: orgId,
      },
    })

    return findDuplicateUsers
  }

  // validate emails onBlur
  const onBlurHandler = () => {
    let noErrors = true
    if (emails.trim() === '') {
      noErrors = false
    }

    const splitEmails = parseEmails(emails)
    // check if the user has enough invites and display error message if not
    if (
      !hasUnlimitedInvites &&
      !!seats &&
      splitEmails.length > seats.available
    ) {
      setValidationMessage(
        `Your plan only has ${seats.available} remaining invite${
          seats.available > 1 ? 's' : ''
        }`
      )
      noErrors = false
    }

    const invalidEmails = findInvalidEmails(splitEmails)
    // check for invalid emails and display error message if any
    if (splitEmails.length > 50 || invalidEmails.length > 0) {
      setValidationMessage(
        invalidEmails.length > 0
          ? 'One or more emails are invalid'
          : 'Only 50 members can be invited at a time'
      )
      noErrors = false
    }

    setReadyToSubmit(noErrors)
    setHasValidationError(!noErrors)
    return !noErrors
  }

  const onSubmitHandler = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // validate emails and invite limit before submitting
    const hasError = onBlurHandler()
    if (hasError) return
    setIsLoading(true)

    // get email Array and remove exact duplicates
    const splitEmails = parseEmails(emails)
    const uniqueEmails = Array.from(new Set(splitEmails))

    const findDuplicateUsers = await getDupeUsers(uniqueEmails)

    if (findDuplicateUsers.emails.length) {
      setHasValidationError(true)
      setValidationMessage(
        `Unable to add ${findDuplicateUsers.emails.join(
          ', '
        )}. To add them, contact support@resilia.com.`
      )
      setIsLoading(false)
      return
    }

    const now = new Date().toISOString().slice(0, 10)

    dispatchTeamMemberInvited({
      role: isFunder ? funderUserRole : resiliaRole,
      accountType: getOptionLabel(roleList, role),
      numberOfMembersInvited: uniqueEmails.length,
      inviterAccountType: isFunder ? funderUserOrgRole : nonprofitOrgRole,
      useWindowLocation: !withDialog,
      invitedTeamMemberDate: now,
    })

    uniqueEmails.forEach((email) => {
      dispatchSpecificTeamMemberAdded({
        accountType: getOptionLabel(roleList, role),
        email: currentUserEmail,
        funderOrgId,
        invitedTeamMember: email,
        invitedTeamMemberDate: now,
        inviterAccountType: isFunder ? funderUserOrgRole : nonprofitOrgRole,
        nonprofitOrgId: orgId,
        platformPlan,
        role: isFunder ? funderUserRole : resiliaRole,
        userStatus: 'Invited',
        useWindowLocation: !withDialog,
      })
    })

    if (isFunder) {
      const createdUsers = await createFunderUsers({
        variables: {
          funderUsersInfo: uniqueEmails.map((email) => ({
            email,
            role,
          })),
          funderOrgId,
          inviterUserId,
        },
      })
    } else {
      await createUsers({
        variables: {
          nonprofitUserInfo: {
            emails: uniqueEmails,
            role,
          },
          nonprofitOrgId: orgId,
          sendInvite: true,
          inviteType: NonprofitOrgInviteType.TeamMember,
        },
      })
    }

    if (callback) callback.function()
  }

  let inviteRemainingCopy = ''
  if (hasUnlimitedInvites) {
    inviteRemainingCopy = `. You have unlimited invites with your plan!`
  } else if (seats) {
    inviteRemainingCopy = `. You have ${seats.available} remaining invite${
      seats?.available > 1 ? 's.' : '.'
    }`
  }

  const renderButtonText = () => {
    let text = ''

    if (isLoading) {
      text = 'Inviting...'
    }
    if (withDialog) {
      text = 'Send Invite(s)'
    } else if (!withDialog) {
      if (successCount) {
        text = `${successCount} Invite${successCount === 1 ? '' : 's'} Sent`
      } else {
        text = 'Send Invite(s)'
      }
    }

    return text
  }

  const renderFormContent = () => (
    <>
      <DialogTitle
        variant='h6'
        style={
          withDialog
            ? {}
            : { textAlign: 'left', padding: '0 0 16px 0', fontSize: '24px' }
        }
      >
        {withDialog
          ? 'Add Team Member(s)'
          : 'Empower Your Staff, Board Members, & Volunteers'}
      </DialogTitle>
      <Content withDialog={withDialog}>
        {withDialog && seats && !isFunder && (
          <AddMembersAlert seats={seats} setOpen={setOpen} />
        )}
        {!withDialog && !isFunder && (
          <OnboardingSection>
            <div className='text'>
              <div style={{ color: 'rgba(0, 0, 0, 0.87)', fontWeight: 600 }}>
                Collective capacity building
              </div>
              <div style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                Learn from nonprofit experts with tailored professional
                development
              </div>
            </div>
            <div>
              <img
                src={AddImage}
                alt='executive director sending course to employee'
              />
            </div>
          </OnboardingSection>
        )}
        <form id='addMembersDialogForm' onSubmit={onSubmitHandler}>
          <Box>
            <FormControl fullWidth margin='normal'>
              <StyledTextfield
                label='Email Address(es)'
                variant='outlined'
                error={hasValidationError}
                required
                helperText={
                  hasValidationError
                    ? validationMessage
                    : `Comma separate emails${
                        withDialog ? '' : inviteRemainingCopy
                      }`
                }
                multiline
                minRows={2}
                maxRows={2}
                onBlur={onBlurHandler}
                value={emails}
                onChange={(e) => {
                  setEmails(e.target.value)
                }}
              />
            </FormControl>

            <FormControl
              fullWidth
              margin='normal'
              sx={{ marginTop: '8px' }}
              required
            >
              <InputLabel id='role-label'>Role</InputLabel>
              <SSelect
                labelId='role-label'
                label='Role'
                variant='outlined'
                value={role}
                onChange={(e) => {
                  const value = e?.target?.value as string
                  setRole(value)
                }}
              >
                {roleList.map(({ value, label, details }) => (
                  <SelectItem
                    key={`item-${value}`}
                    value={value}
                    data-testid={value}
                  >
                    <div>{label.toLowerCase()}</div>
                    <div className='context'>{details}</div>
                  </SelectItem>
                ))}
              </SSelect>
              <FormHelperText>
                <Link
                  href={
                    isFunder
                      ? 'https://help.resilia.com/knowledge/managing-team-members-on-the-funder-platform'
                      : 'https://help.resilia.com/knowledge/add-your-team'
                  }
                  target='_blank'
                >
                  Learn more about role levels
                </Link>{' '}
                in the Resilia Knowledge Base
              </FormHelperText>
            </FormControl>
          </Box>
        </form>
      </Content>

      <DialogActions style={withDialog ? {} : { padding: 0 }}>
        {withDialog && (
          <Button disabled={isLoading} onClick={() => setOpen(false)}>
            Cancel
          </Button>
        )}
        <StyledButton
          variant='contained'
          type='submit'
          form='addMembersDialogForm'
          className={successCount ? 'success' : ''}
          disabled={isLoading || !readyToSubmit || !role}
          style={withDialog ? {} : { width: '100%', marginTop: '8px' }}
        >
          {isLoading && (
            <CircularProgress
              size={20}
              style={{
                color: colors.copy.headerLight,
                marginRight: '20px',
              }}
            />
          )}
          {renderButtonText()}
        </StyledButton>
      </DialogActions>
    </>
  )

  return withDialog ? (
    <SDialog open={open} onClose={() => setOpen(false)}>
      {renderFormContent()}
    </SDialog>
  ) : (
    renderFormContent()
  )
}

const OnboardingSection = styled('div')`
  background: rgba(229, 242, 255, 1);
  border-radius: 4px;
  height: 150px;
  display: flex;
  align-items: center;
  text-align: left;
  font-size: 12.8px;
  margin-bottom: 4px;

  & .text {
    padding: 20px 0 20px 20px;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    height: auto;

    & .text {
      padding: 10px;
    }

    img {
      display: none;
    }
  }
`

const Link = styled('a')`
  text-decoration: none;
  color: ${colors.interactive.primaryEnabled};
`

const SSelect = styled(Select)`
  text-transform: capitalize;

  & .context {
    display: none;
  }

  & li {
    flex-direction: column;
    align-content: flex-start;
  }
`

const SelectItem = styled(MenuItem)`
  & ::first-letter {
    text-transform: capitalize;
  }

  &.MuiMenuItem-root {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  & .context {
    font-size: 12px;
    white-space: initial;
  }

  &:first-child {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }
`

const SDialog = styled(Dialog)`
  & .MuiDialog-paper {
    @media (max-width: 600px) {
      margin: 8px;
    }
  }
`

const Content = styled(DialogContent, {
  shouldForwardProp: (prop) => prop !== 'withDialog',
})<{ withDialog?: boolean }>`
  padding: 20px;
  width: ${({ withDialog }) => (withDialog ? '600px' : '100%')};
  padding: ${({ withDialog }) => (withDialog ? '20px' : '0')};

  @media (max-width: 600px) {
    width: 100%;
  }
`

const StyledButton = styled(Button)`
  &.success {
    pointer-events: none;
    background-color: ${colors.feedback.successDark};
  }
`

const StyledTextfield = styled(TextField)`
  & .MuiOutlinedInput-root {
    padding: 0;
  }

  & textarea {
    padding: 16px 12px;
  }
`
